import { useEffect } from 'react';

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
};

/**
 * Global single-key shortcuts that never fire inside text inputs (Section 14.1):
 * C opens creation, / focuses search. No destructive action is bound globally.
 */
export function useHotkeys(bindings: Record<string, (event: KeyboardEvent) => void>) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey) return;
      const key = `${event.metaKey || event.ctrlKey ? 'mod+' : ''}${event.key.toLowerCase()}`;
      const fn = bindings[key];
      if (!fn) return;
      if (!key.startsWith('mod+') && isTyping(event.target)) return;
      event.preventDefault();
      fn(event);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [bindings]);
}

/** mod+Enter submits the surrounding form (bind on the form's textareas/inputs). */
export function submitOnModEnter(event: React.KeyboardEvent, submit: () => void) {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    event.preventDefault();
    submit();
  }
}
